import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DataLayerStore } from '../../stores/data-layer/data-layer.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { TopicStore } from '../../stores/topic/topic.state';
import { StoryStore } from '../../stores/story/story.state';
import {
  BookmarkInventory,
  InventoryToPublish,
} from '../../models/bookmark-inventory';

export interface PublishRequirementsResult {
  published: InventoryToPublish;
  counts: {
    dataLayers: number;
    indicatorData: number;
    topics: number;
    stories: number;
  };
  /** the view's inventory after publishing */
  inventory: BookmarkInventory;
}

/** Local store whose published flags are kept in sync. */
interface PublishableStore<T> {
  items: () => Record<string, T>;
  overwriteItems: (items: Record<string, T>) => void;
}

/**
 * Makes a view ready for publication: publishes what it still needs (years,
 * indicator data, topics, reports). What exactly gets published is computed
 * on the server from the view's inventory - the client never sends ids.
 */
@Injectable({
  providedIn: 'root',
})
export class BookmarkPublicationService {
  private readonly http = inject(HttpClient);
  private readonly dataLayerStore = inject(DataLayerStore);
  private readonly indicatorDataStore = inject(IndicatorDataStore);
  private readonly topicStore = inject(TopicStore);
  private readonly storyStore = inject(StoryStore);

  /**
   * @param {string} bookmarkId - The view to make ready.
   * @return {Promise<PublishRequirementsResult>} What was published and the updated inventory.
   */
  async publishRequirements(
    bookmarkId: string
  ): Promise<PublishRequirementsResult> {
    const result = await lastValueFrom(
      this.http.post<PublishRequirementsResult>(
        `${environment.apiUrl}/api/bookmark/${bookmarkId}/publish-requirements`,
        {}
      )
    );
    // keep the loaded stores in line with the database
    this.markPublished(this.dataLayerStore, result.published.dataLayerIds);
    this.markPublished(
      this.indicatorDataStore,
      result.published.indicatorDataIds
    );
    this.markPublished(this.topicStore, result.published.topicIds);
    this.markPublished(this.storyStore, result.published.storyIds);
    return result;
  }

  private markPublished<T extends object>(
    store: PublishableStore<T>,
    ids: string[]
  ): void {
    const items = store.items();
    const loaded = ids.filter(id => items[id]);
    if (loaded.length === 0) return;
    const updated = { ...items };
    loaded.forEach(id => {
      updated[id] = { ...items[id], published: true };
    });
    store.overwriteItems(updated);
  }
}
